import React from 'react'
import './UnifiedSettings.css'

/**
 * 통합 설정 컴포넌트 - 제목, 자막, 영상 위치를 한눈에
 */
function UnifiedSettings({ 
  titleSettings, 
  setTitleSettings, 
  subtitleSettings, 
  setSubtitleSettings,
  videoSettings,
  setVideoSettings,
  onTitleSettingsChange,
  onSubtitleSettingsChange,
  onVideoSettingsChange
}) {
  // 제목 설정 업데이트
  const updateTitleSetting = (key, value) => {
    const newSettings = { ...titleSettings, [key]: value }
    setTitleSettings(newSettings)
    if (onTitleSettingsChange) {
      onTitleSettingsChange(newSettings)
    }
  }

  // 자막 설정 업데이트
  const updateSubtitleSetting = (key, value) => {
    const newSettings = { ...subtitleSettings, [key]: value }
    setSubtitleSettings(newSettings)
    if (onSubtitleSettingsChange) {
      onSubtitleSettingsChange(newSettings)
    }
  }

  // 영상 위치 업데이트
  const updateVideoSetting = (key, value) => {
    const newSettings = { ...videoSettings, [key]: value }
    setVideoSettings(newSettings)
    if (onVideoSettingsChange) {
      onVideoSettingsChange(newSettings)
    }
  }

  // 픽셀을 퍼센트로 변환 - 모두 0% = 하단, 100% = 상단으로 통일
  
  // 자막 위치: 높은 픽셀값 = 하단, 낮은 픽셀값 = 상단
  // 1920px = 0% (하단), 0px = 100% (상단)
  const subtitlePositionPercent = Math.round((1 - (subtitleSettings.yPosition || 1500) / 1920) * 100)
  
  // 제목 위치: 1080px = 0% (하단), 0px = 100% (상단) 
  const titlePositionPercent = Math.round((1 - (titleSettings.yPosition || 100) / 1080) * 100)
  
  // 영상 위치: -200 = 100% (상단), +200 = 0% (하단)
  const videoPositionPercent = videoSettings?.videoYOffset 
    ? Math.round(50 - (videoSettings.videoYOffset / 4)) 
    : 50

  return (
    <div className="unified-settings">
      <div className="settings-header">
        <h4>미디어 설정</h4>
        <div className="settings-subtitle">제목, 자막, 영상 위치를 조정하세요</div>
      </div>

      <div className="settings-grid">
        {/* 제목 설정 카드 */}
        <div className="setting-card">
          <div className="card-header">
            <span className="card-title">제목</span>
            <label className="toggle-compact">
              <input
                type="checkbox"
                checked={titleSettings.enabled !== false}
                onChange={(e) => updateTitleSetting('enabled', e.target.checked)}
              />
              <span className="toggle-slider-compact"></span>
            </label>
          </div>
          
          {titleSettings.enabled !== false && (
            <div className="card-controls">
              <div className="control-row">
                <span className="control-label">크기</span>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={Math.round(((titleSettings.fontSize || 65) - 40) / 110 * 100)}
                  onChange={(e) => {
                    const percent = parseInt(e.target.value)
                    const fontSize = Math.round(40 + (percent / 100) * 110)
                    updateTitleSetting('fontSize', fontSize)
                  }}
                  className="range-horizontal"
                />
                <span className="control-value">{titleSettings.fontSize || 65}</span>
              </div>
              
              <div className="control-row">
                <span className="control-label">위치</span>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={titlePositionPercent}
                  onChange={(e) => {
                    const percent = parseInt(e.target.value)
                    // 0% = 하단(1080px), 100% = 상단(0px)
                    const yPosition = Math.round((1 - percent / 100) * 1080)
                    updateTitleSetting('yPosition', yPosition)
                  }}
                  className="range-horizontal"
                />
                <span className="control-value">{titlePositionPercent}%</span>
              </div>
            </div>
          )}
        </div>

        {/* 자막 설정 카드 */}
        <div className="setting-card">
          <div className="card-header">
            <span className="card-title">자막</span>
            <label className="toggle-compact">
              <input
                type="checkbox"
                checked={subtitleSettings.enabled !== false}
                onChange={(e) => updateSubtitleSetting('enabled', e.target.checked)}
              />
              <span className="toggle-slider-compact"></span>
            </label>
          </div>
          
          {subtitleSettings.enabled !== false && (
            <div className="card-controls">
              <div className="control-row">
                <span className="control-label">크기</span>
                <input 
                  type="range" 
                  min="24" 
                  max="80" 
                  value={subtitleSettings.fontSize}
                  onChange={(e) => updateSubtitleSetting('fontSize', parseInt(e.target.value))}
                  className="range-horizontal"
                />
                <span className="control-value">{subtitleSettings.fontSize}</span>
              </div>
              
              <div className="control-row">
                <span className="control-label">위치</span>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={subtitlePositionPercent}
                  onChange={(e) => {
                    const percent = parseInt(e.target.value)
                    // 0% = 하단(1920px), 100% = 상단(0px)
                    const pixelValue = Math.round((1 - percent / 100) * 1920)
                    updateSubtitleSetting('yPosition', pixelValue)
                  }}
                  className="range-horizontal"
                />
                <span className="control-value">{subtitlePositionPercent}%</span>
              </div>
            </div>
          )}
        </div>

        {/* 영상 위치 카드 */}
        <div className="setting-card">
          <div className="card-header">
            <span className="card-title">영상</span>
            <button 
              className="reset-btn"
              onClick={() => updateVideoSetting('videoYOffset', 0)}
              title="초기화"
            >
              ↻
            </button>
          </div>
          
          <div className="card-controls">
            <div className="control-row">
              <span className="control-label">세로</span>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={videoPositionPercent}
                onChange={(e) => {
                  const value = parseInt(e.target.value)
                  // 0% = 하단(+200), 100% = 상단(-200)
                  const yOffset = (50 - value) * 4
                  updateVideoSetting('videoYOffset', yOffset)
                }}
                className="range-horizontal"
              />
              <span className="control-value">{videoPositionPercent}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UnifiedSettings